import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Download, MessageCircle, Mail, Link2, ArrowLeft, Loader2, Pill, Edit, Files, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";

const STATUS_STYLE = {
  issued: "bg-blue-50 text-blue-700", draft: "bg-slate-100 text-slate-600", sent: "bg-indigo-50 text-indigo-700",
  received: "bg-amber-50 text-amber-700", processing: "bg-amber-50 text-amber-700", ready: "bg-emerald-50 text-emerald-700",
  dispensed: "bg-emerald-50 text-emerald-700", cancelled: "bg-red-50 text-red-600", expired: "bg-red-50 text-red-600",
};

export default function PrescriptionView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rx, setRx] = useState(null);
  const [loading, setLoading] = useState(true);
  const origin = window.location.origin;

  useEffect(() => { api.get(`/prescriptions/${id}`).then((r) => setRx(r.data)).catch(() => toast.error("Not found")).finally(() => setLoading(false)); }, [id]);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!rx) return <div className="p-8 text-center text-slate-500">Prescription not found.</div>;

  const pdfUrl = `${API}/prescriptions/${id}/pdf?origin=${encodeURIComponent(origin)}&token=${rx.public_token}`;
  const verifyLink = `${origin}/verify/${rx.public_token}`;
  const medText = rx.medications?.map((m) => `${m.name} ${m.strength}`).join(", ");
  const waMsg = encodeURIComponent(`Prescription ${rx.prescription_number}\nPatient: ${rx.patient?.first_name} ${rx.patient?.last_name}\nMedications: ${medText}\n\nOpen prescription PDF: ${pdfUrl}\nVerify: ${verifyLink}`);
  const waNumber = rx.pharmacy_snapshot?.whatsapp?.replace(/\D/g, "");
  const emailHref = (to) => `mailto:${to}?subject=${encodeURIComponent(`Prescription ${rx.prescription_number}`)}&body=${encodeURIComponent(`Prescription details.\n\nPatient: ${rx.patient?.first_name} ${rx.patient?.last_name}\nMedications: ${medText}\n\nVerify securely: ${verifyLink}`)}`;
  const emailRx = async (to, label) => {
    if (!to) { toast.error(`No ${label} email on file`); return; }
    try { await api.post(`/prescriptions/${id}/email`, { to, origin, recipient_type: label }); toast.success(`Prescription emailed to ${label}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send email"); }
  };

  const duplicate = async () => {
    try {
      const payload = { patient: rx.patient, medications: rx.medications, allergies: rx.allergies, notes: rx.notes, pharmacy_id: rx.pharmacy_id, patient_chooses: rx.patient_chooses, status: "draft" };
      const res = await api.post("/prescriptions", payload);
      toast.success("Duplicated as draft"); navigate(`/prescriptions/${res.data.id}/edit`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not duplicate"); }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-800 text-sm mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-head">{rx.patient?.first_name} {rx.patient?.last_name}</h1>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_STYLE[rx.status]}`}>{rx.status}</span>
          </div>
          <p className="text-slate-500 mt-1">{rx.prescription_number} · {rx.created_at?.slice(0, 10)}</p>
        </div>
        {user?.role === "doctor" && (
          <div className="flex gap-2">
            <Button data-testid="rx-edit-btn" variant="outline" onClick={() => navigate(`/prescriptions/${id}/edit`)} className="rounded-xl"><Edit className="w-4 h-4 mr-1" /> Edit</Button>
            <Button data-testid="rx-duplicate-btn" variant="outline" onClick={duplicate} className="rounded-xl"><Files className="w-4 h-4 mr-1" /> Duplicate</Button>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <p className="text-xs font-semibold tracking-[0.15em] uppercase text-blue-600 mb-4">Medications</p>
            {rx.allergies && <p className="text-sm text-red-600 font-medium mb-3">⚠ Allergies: {rx.allergies}</p>}
            <div className="space-y-3">
              {rx.medications?.map((m, i) => (
                <div key={i} className="border border-slate-100 rounded-xl p-3">
                  <p className="font-semibold text-slate-900">{i + 1}. {m.name} {m.strength}</p>
                  <p className="text-sm text-slate-500 mt-0.5">{m.dosage_form} · {m.route} · Qty {m.quantity || "—"} · Refills {m.refills}</p>
                  <p className="text-sm text-slate-700 mt-0.5">Sig: {m.frequency} {m.duration && `for ${m.duration}`}. {m.instructions}</p>
                </div>
              ))}
            </div>
            {rx.notes && <p className="text-sm text-slate-600 mt-4"><span className="text-slate-400">Notes:</span> {rx.notes}</p>}
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <p className="text-xs font-semibold tracking-[0.15em] uppercase text-blue-600 mb-4">Prescriber</p>
            <p className="text-sm font-medium text-slate-800">{rx.doctor_snapshot?.full_name} · {rx.doctor_snapshot?.medical_reg_number}</p>
            <p className="text-sm text-slate-500">{rx.doctor_snapshot?.clinic_name}</p>
            {rx.doctor_snapshot?.signature_file_id && <img src={`${API}/files/${rx.doctor_snapshot.signature_file_id}`} alt="signature" className="h-16 mt-2 object-contain" />}
          </div>
        </div>
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-semibold text-slate-900 font-head flex items-center gap-2"><Pill className="w-5 h-5 text-blue-600" /> Prescription PDF</h3>
            <a href={pdfUrl} download target="_blank" rel="noreferrer"><Button data-testid="rx-download-pdf" className="w-full mt-4 rounded-xl h-12 bg-blue-600 hover:bg-blue-700"><Download className="w-4 h-4 mr-1.5" /> Download PDF</Button></a>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-semibold text-slate-900 font-head">Share</h3>
            <div className="space-y-2.5 mt-4">
              {waNumber && <a href={`https://wa.me/${waNumber}?text=${waMsg}`} target="_blank" rel="noreferrer"><Button data-testid="rx-wa-pharmacy" variant="outline" className="w-full rounded-xl h-11 justify-start border-slate-200"><MessageCircle className="w-4 h-4 mr-2 text-emerald-600" /> WhatsApp Pharmacy</Button></a>}
              <a href={`https://wa.me/?text=${waMsg}`} target="_blank" rel="noreferrer"><Button data-testid="rx-wa-patient" variant="outline" className="w-full rounded-xl h-11 justify-start border-slate-200 mt-2.5"><MessageCircle className="w-4 h-4 mr-2 text-emerald-600" /> WhatsApp Patient</Button></a>
              <Button data-testid="rx-email-patient" onClick={() => emailRx(rx.patient?.email, "patient")} variant="outline" className="w-full rounded-xl h-11 justify-start border-slate-200 mt-2.5"><Mail className="w-4 h-4 mr-2 text-blue-600" /> Email Patient</Button>
              <Button data-testid="rx-email-pharmacy" onClick={() => emailRx(rx.pharmacy_snapshot?.email, "pharmacy")} variant="outline" className="w-full rounded-xl h-11 justify-start border-slate-200 mt-2.5"><Mail className="w-4 h-4 mr-2 text-blue-600" /> Email Pharmacy</Button>
              <Button data-testid="rx-copy-link" onClick={async () => { try { await navigator.clipboard.writeText(pdfUrl); } catch (e) {} toast.success("Secure PDF link copied"); }} variant="outline" className="w-full rounded-xl h-11 justify-start border-slate-200 mt-2.5"><Link2 className="w-4 h-4 mr-2 text-slate-600" /> Copy View Link</Button>
            </div>
          </div>
          {rx.pharmacy_snapshot && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <h3 className="font-semibold text-slate-900 font-head">Pharmacy</h3>
              <p className="text-sm font-medium text-slate-800 mt-2">{rx.pharmacy_snapshot.name}</p>
              <p className="text-sm text-slate-500">{rx.pharmacy_snapshot.address}</p>
              <p className="text-sm text-slate-500">{rx.pharmacy_snapshot.phone}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
